import type { UnionToIntersection } from 'type-fest';
import { Domain } from '../../domain';

import D = Domain.Object;

export type AuthorizationAuthInfo = {
  Account: {
    id: D.Account.Id;
    hasActiveSystemAdmin: boolean;
    isEmployee: boolean;
    isSystemAdminEligible: boolean;
    isPiiEligible: boolean;
    hasActivePiiAccess: boolean;
  };
  Organization: {
    id: D.Organization.Id;
    role:
      | {
          key: D.Organization.Role['key'];
        }
      | undefined;
    authRequirements: D.AuthRequirements;
    projects: {
      id: D.Project.Id;
      group?: {
        isProjectAdmin: boolean;
      } | null;
      authRequirements: D.AuthRequirements;
    }[];
  }[];
};

/**
 * 関数を合成して、それらの関数がすべて true を返すかどうかをチェックする関数を返す
 * ex)
 * const fn1 = (props: { authInfo: AuthorizationAuthInfo; organizationId: D.Organization.Id }) => hasOrganizationAdminRole(props);
 * const fn2 = (props: { authInfo: AuthorizationAuthInfo; projectId: D.Project.Id }) => hasProjectAdminRole(props);
 * const fn = Synth.And(fn1, fn2);
 * fn({ authInfo, organizationId, projectId });
 */
export namespace Synth {
  // biome-ignore lint/suspicious/noExplicitAny: ok
  type AnyConditionFunction = (params: any) => boolean;
  type ParamsType<Fns extends AnyConditionFunction[]> = UnionToIntersection<Parameters<Fns[number]>[0]>;

  export const And = <Fns extends AnyConditionFunction[]>(...fns: Fns) => {
    return (params: ParamsType<Fns>) => fns.every((fn) => fn(params));
  };
  export const Or = <Fns extends AnyConditionFunction[]>(...fns: Fns) => {
    return (params: ParamsType<Fns>) => fns.some((fn) => fn(params));
  };
}

export const IsNotMyAccount = (props: { authInfo: AuthorizationAuthInfo; accountId: D.Account.Id }) => {
  const { authInfo, accountId } = props;
  return authInfo.Account.id !== accountId;
};

/**
 * Auth API でアクティブなシステム管理者権限を要求する判定の source of truth。
 * eligibility (`system_admin.eligible`) は有効化を申請できることだけを表し、
 * システム管理者操作を実行する権限として扱ってはならない。
 */
export const IsSystemAdmin = (props: { authInfo: AuthorizationAuthInfo }) => {
  return props.authInfo.Account.hasActiveSystemAdmin;
};
export const IsEmployee = (props: { authInfo: AuthorizationAuthInfo }) => props.authInfo.Account.isEmployee;
/** システム管理者権限の有効化を申請できる状態。アクティブなシステム管理者権限ではない。 */
export const IsSystemAdminEligible = (props: { authInfo: AuthorizationAuthInfo }) => props.authInfo.Account.isSystemAdminEligible;
/** PII アクセスの有効化を申請できる状態。アクティブな PII アクセスではない。 */
export const IsPiiEligible = (props: { authInfo: AuthorizationAuthInfo }) => props.authInfo.Account.isPiiEligible;
/** アカウント単位の一時 PII グラントが有効かどうか。プロダクト別の PII 権限判定には productKey 付きの internal-api getProjectAuthInfo を使うこと。 */
export const HasActivePiiAccess = (props: { authInfo: AuthorizationAuthInfo }) => props.authInfo.Account.hasActivePiiAccess;

export const IsOrganizationに所属している = (props: { authInfo: AuthorizationAuthInfo; organizationId: D.Organization.Id }) => {
  const { authInfo, organizationId } = props;
  return authInfo.Organization.some((o) => o.id === organizationId);
};

export const hasOrganizationAdminRole = (props: { authInfo: AuthorizationAuthInfo; organizationId: D.Organization.Id }) => {
  const { authInfo, organizationId } = props;
  const organization = authInfo.Organization.find((o) => o.id === organizationId);
  return organization?.role?.key === 'admin';
};

export const IsProjectに所属している = (props: { authInfo: AuthorizationAuthInfo; projectId: D.Project.Id }) => {
  const { authInfo, projectId } = props;
  return authInfo.Organization.some((o) => o.projects.some((p) => p.id === projectId));
};

export const hasProjectAdminRole = (props: { authInfo: AuthorizationAuthInfo; projectId: D.Project.Id }) => {
  const { authInfo, projectId } = props;
  const project = authInfo.Organization.flatMap((o) => o.projects).find((p) => p.id === projectId);
  return project?.group?.isProjectAdmin === true;
};

/** 当該 org に非所属なら true（強制は所属メンバーにのみ効く）。 */
export const IsSsoRequirementSatisfied = (props: { authInfo: AuthorizationAuthInfo; organizationId: D.Organization.Id }) => {
  const { authInfo, organizationId } = props;
  const organization = authInfo.Organization.find((o) => o.id === organizationId);
  if (!organization) return true;
  return !organization.authRequirements.sso.required || organization.authRequirements.sso.satisfied;
};

/** 当該 project の org に非所属なら true。 */
export const IsSsoRequirementSatisfiedForProject = (props: { authInfo: AuthorizationAuthInfo; projectId: D.Project.Id }) => {
  const { authInfo, projectId } = props;
  const project = authInfo.Organization.flatMap((o) => o.projects).find((p) => p.id === projectId);
  if (!project) return true;
  return !project.authRequirements.sso.required || project.authRequirements.sso.satisfied;
};

// biome-ignore lint/suspicious/noExplicitAny: orgScoped / projectScoped は任意の Can 述語を包む
export const orgScoped = <Fn extends (params: any) => boolean>(fn: Fn) => Synth.And(IsSsoRequirementSatisfied, fn);

// biome-ignore lint/suspicious/noExplicitAny: orgScoped / projectScoped は任意の Can 述語を包む
export const projectScoped = <Fn extends (params: any) => boolean>(fn: Fn) => Synth.And(IsSsoRequirementSatisfiedForProject, fn);

/** プロジェクト管理者、対象組織の組織管理者、またはシステム管理者。organizationId と projectId の両方が必要。 */
export const hasProjectManagementAccess = Synth.Or(
  projectScoped(Synth.Or(IsSystemAdmin, hasProjectAdminRole)),
  orgScoped(Synth.Or(IsSystemAdmin, hasOrganizationAdminRole)),
);

/**
 * 設定画面への導線（歯車・メニューリンク）の表示判定。
 * SSO / MFA は見ない（遷移先のルートガードで判定する）。
 */
export const canShowOrganizationSettingsLink = (props: { authInfo: AuthorizationAuthInfo; organizationId: D.Organization.Id }) =>
  IsSystemAdmin(props) || hasOrganizationAdminRole(props);

/**
 * 設定画面への導線（歯車・メニューリンク）の表示判定。
 * SSO / MFA は見ない（遷移先のルートガードで判定する）。
 */
export const canShowProjectSettingsLink = (props: {
  authInfo: AuthorizationAuthInfo;
  organizationId: D.Organization.Id;
  projectId: D.Project.Id;
}) =>
  IsSystemAdmin(props) || hasOrganizationAdminRole(props) || hasProjectAdminRole({ authInfo: props.authInfo, projectId: props.projectId });
